import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useProductsSummaryAnalytics from "@babackapp/private-api-client/client/use-products-summary-analytics";
import { ProductAnalyticsItem } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import { ScrollArea } from "@babackapp/ui/components/scroll-area";
import { useDashboardFilters } from "../lib/dashboard-filters";
import useFormattedTimeRange from "../lib/use-formatted-time-range";
import { ProductListItem } from "./product-list-item";
import { ProductSelectSearch } from "@/pages/(app)/home/components/product-select-search.tsx";
import { useState } from "react";

type ProductVariantsListProps = {
  product: ProductAnalyticsItem;
  onSelectVariant: (variantId: string) => void;
};

function ProductVariantsList({
  product,
  onSelectVariant,
}: ProductVariantsListProps) {
  return product.variants.map((variant) => (
    <ProductListItem
      key={variant.variantId}
      name={variant.title}
      subtitle={`(${product.title})`}
      image={product.image}
      count={variant.count}
      sku={variant.sku}
      onClick={() => onSelectVariant(variant.variantId)}
    />
  ));
}

type ProductListProps = {
  products: ProductAnalyticsItem[];
  onSelectProduct: (productId: string) => void;
};

function ProductList({ products, onSelectProduct }: ProductListProps) {
  return products?.map((product) => (
    <ProductListItem
      key={product.id}
      name={product.title}
      image={product.image}
      count={product.count}
      onClick={() => onSelectProduct(product.id)}
    />
  ));
}

type ReturnRequestProductsListProps = {
  setProductFilter: (
    productId: string | undefined,
    variantId: string | undefined,
  ) => void;
};

export function ReturnRequestProductsList({
  setProductFilter,
}: ReturnRequestProductsListProps) {
  const { data: shop } = useCurrentShopSuspense();
  const t = useTranslation();
  const [filteredProducts, setFilteredProducts] = useState<
    ProductAnalyticsItem[]
  >([]);

  const {
    fromTimestamp,
    toTimestamp,
    productId,
    variantId,
    resolution,
    reason,
  } = useDashboardFilters();
  const { data: productsAnalytics } = useProductsSummaryAnalytics(shop.id, {
    fromTimestamp,
    toTimestamp: toTimestamp ?? 0,
    productId,
    variantId,
    resolution,
    reason,
  });

  const timeRange = useFormattedTimeRange();

  const selectedProduct = (productsAnalytics ?? []).find(
    (p) => p.id === productId,
  );

  // If a product is selected, we don't need to use filteredProducts
  const displayProducts = selectedProduct
    ? (productsAnalytics ?? [])
    : filteredProducts.length > 0
      ? filteredProducts
      : (productsAnalytics ?? []);

  return (
    <Card className="col-span-2">
      <CardHeader className="pb-2">
        <CardTitle className="text-md">
          {t("analyticsDashboard.producstList.title")}
        </CardTitle>
        <CardDescription>{timeRange}</CardDescription>
      </CardHeader>
      <CardContent>
        <ProductSelectSearch
          setProductFilter={setProductFilter}
          onFilteredProductsChange={setFilteredProducts}
          products={productsAnalytics ?? []}
        />
        <ScrollArea className="h-96 mt-4">
          <div className="flex flex-col gap-2">
            {selectedProduct ? (
              <ProductVariantsList
                product={selectedProduct}
                onSelectVariant={(variantId) =>
                  setProductFilter(productId, variantId)
                }
              />
            ) : (
              <ProductList
                products={displayProducts}
                onSelectProduct={(productId) =>
                  setProductFilter(productId, undefined)
                }
              />
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
